<template>
    <div class="input-wrapper select-component" ref="selectComponent">
        <div class="select-wrapper styled-select">
            <div
                :class="[value && 'filled', showOptions && 'focus']"
                class="input select-input"
                @click="showOptions = !showOptions"
            >
                {{ selectedOption }}
            </div>

            <FloatingLabel :label="displayName" :icon="icon" />

            <div class="select-arrow">
                <i class="fas fa-angle-down"></i>
            </div>
        </div>

        <ul class="select-options" v-show="showOptions && options.length">
            <li v-for="(option, index) in options" :key="option.value" @click="selectOption(option.value)">
                <template v-if="index != options.length - 1">
                    {{ option.name }}
                </template>

                <template v-else>
                    <IntersectObserver @intersect="intersected" />
                    {{ option.name }}
                </template>
            </li>

            <li v-if="!hideScrollGradient" class="gradient-line"></li>
        </ul>

        <Field type="hidden" :name="name" v-model="value" />
        <div class="error" :class="{ 'reveal-error': displayErrorMessage }">{{ displayErrorMessage }}</div>
    </div>
</template>

<script>
import { useField, Field } from 'vee-validate'
import { useStore } from 'vuex'
import { titleCase } from '@js/utilities/language.js'

// lodash components
import find from 'lodash/find'
import _get from 'lodash/get'

export default {
    components: {
        Field,
    },
    data() {
        return {
            showOptions: false,
            hideScrollGradient: false,
        }
    },
    mounted() {
        if (this.value) {
            this.selectOption(this.value)
        }
    },
    props: {
        customErrorMessage: String,
        icon: String,
        label: String,
        name: String,
        validationRules: Object,
        vuexKey: String,
        options: {
            default: () => {},
        },
        defaultOption: {
            default: () => {},
        },
    },
    setup(props) {
        const store = useStore()
        const { errorMessage, validate, resetField } = useField(props.name, props.validationRules, {
            label: titleCase(props.name),
        })

        return {
            errorMessage,
            validate,
            resetField,
            store,
        }
    },
    watch: {
        showOptions: function (value) {
            if (value) {
                document.addEventListener('click', this.closeIfClickedOutside)
            } else {
                document.removeEventListener('click', this.closeIfClickedOutside)
            }
        },
    },
    computed: {
        value: {
            get() {
                const defaultValue = this.defaultOption !== undefined ? this.defaultOption : null
                const value = this.store.getters.fieldValue(this.name, this.vuexKey || 'data', !!this.vuexKey) || defaultValue

                this.resetField({
                    value: value,
                })

                return value
            },
        },
        selectedOption: function () {
            if (!this.value) {
                return null
            }

            const option = find(this.options, ['value', this.value])
            const foundOption = option && option.name

            if (foundOption) {
                return option.name
            }

            this.selectOption(null)

            return null
        },
        displayName: function () {
            return this.label || this.titleCase(this.name)
        },
        serverError() {
            return this.store.getters.fieldServerError(this.name)
        },
        displayErrorMessage() {
            return this.errorMessage && !this.showOptions
                ? this.customErrorMessage || this.errorMessage
                : this.serverError
        },
    },
    methods: {
        selectOption: function (value) {
            if (this.vuexKey) {
                this.store.commit('setDataFieldValue', {
                    name: this.name,
                    value: value,
                    key: this.vuexKey,
                })
            } else {
                this.store.dispatch('setFieldValue', {
                    name: this.name,
                    value: value
                })
            }

            this.showOptions = false

            if (value) {
                this.$nextTick(async () => {
                    this.valid = await this.validate()
                })
            }
        },
        intersected: function (intersecting) {
            this.hideScrollGradient = intersecting
        },
        closeIfClickedOutside: function (event) {
            const closestElement = event.target.closest('.select-component')

            if (!closestElement || !closestElement.isSameNode(this.$refs.selectComponent)) {
                this.showOptions = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.input-wrapper {
    .select-wrapper {
        position: relative;
        height: 100%;

        .input {
            background: #ffffff;
            border-radius: 4px;
            border: 1px solid #e0e1e5;
            color: var(--input-text-color);
            cursor: pointer;
            display: inline-block;
            font-size: rem(16px);
            height: 53px;
            margin: 0;
            margin-bottom: -7px;
            overflow: hidden;
            position: relative;
            text-shadow: none;
            width: 100%;

            &.filled {
                margin-bottom: 0px;
            }
        }

        .select-arrow {
            color: #b3b9c5;
            pointer-events: none;
            position: absolute;
            right: 5%;
            top: 50%;
            transform: translateY(-50%);
        }
    }

    .select-options {
        background-color: #ffffff;
        border-radius: 4px;
        border: 1px solid #e0e1e5;
        bottom: 0;
        max-height: 280px;
        overflow: scroll;
        position: absolute;
        transform: translate(0, 100%);
        width: 100%;
        z-index: 1000;

        li {
            background: #ffffff;
            font-size: rem(16px);
            margin-bottom: 1px;
            padding: 15px 10px;
            position: relative;

            &:after {
                border-bottom: 1px solid #e0e1e5;
                bottom: -1px;
                content: ' ';
                left: 10px;
                position: absolute;
                width: calc(100% - 20px);
            }

            &:last-of-type {
                margin-bottom: 0;

                &:after {
                    display: none;
                }
            }

            &:hover {
                background: #e0e1e5;
                cursor: pointer;
            }
        }

        .gradient-line {
            background: linear-gradient(
                180deg,
                rgba(255, 255, 255, 0) 0%,
                rgba(255, 255, 255, 1) 79%,
                rgba(255, 255, 255, 1) 99%
            );
            bottom: 0;
            height: 60px;
            position: sticky;
        }
    }

    .error {
        @media screen and (max-width: $bp-large) {
            display: none;
            &.reveal-error {
                display: block;
            }
        }

        @media screen and (min-width: $bp-large) {
            height: 0px;
            overflow: hidden;
            padding: 0px 5px 0px 22px;
            transition: all 0.7s ease;

            &.reveal-error {
                height: 27px;
                padding: 2px 5px 5px 22px;
            }
        }
    }
}
</style>
